'use client';

import { useEffect, useState } from 'react';
import { CONSTANT } from '@const';
import { msalService } from '@services';

/**
 * Custom hook to manage session and token expiry
 * @returns {Object} Object containing sessionTimedOut state and clearSession function
 */
export const useSessionManager = () => {
	const isTokenActive = msalService.validateTokenLifetime(msalService.getTokenExpiration());
	const [isSessionTimedOut, setIsSessionTimedOut] = useState(false);

	const updateSessionExpiry = () => {
		const expiryTime = Date.now() + CONSTANT.SESSION_TIMEOUT;
		// console.log(expiryTime);
		localStorage.setItem('sessionExpiry', expiryTime.toString());
	};

	const clearSession = () => {
		localStorage.removeItem('sessionExpiry');
		setIsSessionTimedOut(false);
	};

	const refreshToken = async () => {
		await msalService.forceTokenRefresh().catch(() => {
			setIsSessionTimedOut(true);
			return;
		});
	};

	/**
	 * Check session expiry and refresh token if necessary
	 */
	const checkSessionAndRefreshToken = async () => {
		const currentTime = Date.now();
		const sessionExpiryTime = Number(localStorage.getItem('sessionExpiry'));

		// Timeout session if session expiry is less than current time
		if (sessionExpiryTime && sessionExpiryTime < currentTime) {
			setIsSessionTimedOut(true);
			return;
		}

		// Refresh if session is active but access token has expired
		const tokenExpiration = msalService.getTokenExpiration();
		const isTokenExpired = tokenExpiration <= currentTime;

		// TUO sessions time-out after ~20-30 minutes of inactivty - each time we request a refresh token the countdown is restarted
		// if the TUO session times out our log-out will not action will not be handled correctly by TUO
		const lastLastTokenEndpointInteraction = msalService.getLastTokenEndpointInteraction();
		if (
			isTokenExpired ||
			(lastLastTokenEndpointInteraction ?? 0) > CONSTANT.SERVICE_INACTIVITY_TIMEOUT
		) {
			await refreshToken();
		}
	};

	/**
	 * Initial session expiry setup when user is logged in with valid token
	 */
	useEffect(() => {
		if (isTokenActive) {
			updateSessionExpiry();
		}
	}, [isTokenActive]); // eslint-disable-line react-hooks/exhaustive-deps

	/**
	 * Event listeners to update session expiry on user activity
	 */
	useEffect(() => {
		if (isTokenActive) {
			const events = ['click', 'keydown', 'scroll', 'mousemove', 'touchstart', 'focusin'];
			events.forEach(event => window.addEventListener(event, updateSessionExpiry));

			// Cleanup function
			return () => {
				events.forEach(event => window.removeEventListener(event, updateSessionExpiry));
			};
		}
	}, [isTokenActive]); // eslint-disable-line react-hooks/exhaustive-deps

	/**
	 * Set up interval to regularly check session and token status
	 */
	useEffect(() => {
		if (isTokenActive) {
			const intervalCheck = setInterval(() => {
				checkSessionAndRefreshToken();
			}, CONSTANT.SESSION_CHECK_INTERVAL);

			return () => {
				clearInterval(intervalCheck);
			};
		}
	}, [isTokenActive]); // eslint-disable-line react-hooks/exhaustive-deps

	return { isSessionTimedOut, clearSession };
};
